import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

type PageRoute = '/' | '/chat' | '/features' | '/docs' | '/pricing' | '/about' | '/agreement' | '/training' | '/configuration' | '/mission' | '/qrcodehub' | '/agent-models' | '/broadcasting-demo' | '/agent-architecture' | '/connected-collaboration-demo' | '/my-purchases' | '/analytics' | '/unified-agent';

interface NavigationContextType {
  currentPage: PageRoute;
  navigate: (path: PageRoute) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

// Helper function to determine initial route from URL parameters
function getInitialRoute(): PageRoute {
  if (typeof window === 'undefined') return '/unified-agent';
  
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view');
  const agent = params.get('agent');
  
  // Handle view=workforce or agent=open parameters
  if (view === 'workforce' || agent === 'open') {
    return '/unified-agent';
  }
  
  return '/unified-agent';
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [currentPage, setCurrentPage] = useState<PageRoute>(getInitialRoute());

  // Handle URL parameter changes for view parameter
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('view');
      const agent = params.get('agent');
      
      // Route based on view parameter
      if (view === 'workforce' || agent === 'open') {
        setCurrentPage('/unified-agent');
      }
    }
  }, []);

  const navigate = (path: PageRoute) => {
    setCurrentPage(path);
  };

  return (
    <NavigationContext.Provider value={{ currentPage, navigate }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
}